import { useCallback, useContext, useEffect, useState } from "react"
import { AppContext } from "../../../App"
import { ChartEvents } from "../chart.events"
import { useDispatch } from "react-redux"
import { openConnectAccountDialog, updateOrderHistory } from "../../../reducer/orders.reducer"
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from "../../../proto/generated/order"
import { useSelector } from "react-redux"
import { selectPrimarySecurity } from "../../../selectors/home"
import { PositionComponentTypes } from "../drawingTools/ordersOnChartTool/OrdersOnChartTool"
import { Position } from "../../../proto/generated/position"
import { Security } from "../../../proto/generated/candle-data-depot-common"
import { selectPrimaryTradingAccount, selectTradingAccounts } from "../../../selectors/user"
import { cancelOrder, placeOrModifyOrder } from "../../../services/apis/orders"
import commonActions from "../../../actions/common.actions"
import ReusableDialog from "../../common/reusableDialog"
import { getSecurityAsync } from "../../../util/security"
import { ExtraWideTooltip } from "../../custom"
import { Avatar, Box, Card, CardContent, CardHeader, Typography } from "@mui/material"
import { getDisplayDateTime } from "../../../util/dateTime"
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from "../../../proto/generated/trading-bridge"
import { addOrderAlertMessage } from "../../../util/orders"
import { v4 as uuid } from "uuid"
import { selectIsEmbedded } from "../../../selectors/device"
import { callNative } from "../../../util/nativeBridge"
import { selectPositions } from "../../../selectors/positions"

type Props = {
    chartId: string
}

const OrderActionsOnChart = ({ chartId }: Props) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(chartId)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const tradingAccounts = useSelector(selectTradingAccounts)
    const primarySecurity = useSelector(selectPrimarySecurity)
    const { key: primaryKey } = primarySecurity
    const [editOrderData, setEditOrderData] = useState<{
        orders: Order[]
        type: 'reverse' | 'cancel' | 'close' | 'place'
    } | undefined>()
    const [requestInProgres, setRequestInProgress] = useState(false)
    const isEmbedded = useSelector(selectIsEmbedded)
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (primaryKey) getSecurityAsync(primaryKey).then(setSecurity) }, [primaryKey])
    const [ordersMarkerData, setOrdersMarkerData] = useState<{
        x: number
        y: number
        totalQuantity: number
        averagePrice: number
        type: 'buy' | 'sell'
        time: number
        orders: Order[]
    } | null>(null)
    const positions = useSelector(selectPositions)

    const handleEditOrder = useCallback(async (data: {
        orders: Order[],
        type: 'reverse' | 'cancel' | 'close' | 'place'
    }) => {
        if (!primaryTradingAccount || !tradingAccounts || tradingAccounts?.length === 0) {
            dispatch(openConnectAccountDialog({
                type: 'connect',
                tradingAccountId: ''
            }))
        } else if (primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            dispatch(openConnectAccountDialog({
                type: 'reconnect',
                tradingAccountId: primaryTradingAccount.accountId
            }))
        }
        // else if (!confirmedFromSurveillance && isSecurityUnderSurveillance) {
        //     setShowSurveillanceDialog(true);
        //     return;
        // }
        if (!data?.orders?.length) return;
        if (data.type === 'cancel') {
            const res = await cancelOrder({
                dispatch,
                body: {
                    tradingAccountId: primaryTradingAccount?.accountId ?? "",
                    orderId: data.orders.map(d => d.orderId)
                }
            });
            const responseOrder = res.orderResponse[0];
            if (!responseOrder) return;
            const { successful, commitOrder, errorMessage } = responseOrder;

            if (!successful) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: errorMessage || 'Unable to cancel order',
                    errorType: 'error'
                })

                return;
            } else if (commitOrder) {
                dispatch(updateOrderHistory({ updates: data.orders.map(order => ({ ...order, status: OrderStatus.CANCELLED })) }))
            }
        } else {
            const requestOrders: Order[] = data.orders
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId ?? '',
                order: requestOrders,
                buyBeforeSell: false
            }

            try {
                setRequestInProgress(true);
                const response = await placeOrModifyOrder(request);
                const updates: Order[] = [];
                let successCount = 0;
                (response.orderResponse || []).forEach((orderResponse, i) => {
                    const { successful, commitOrder, brokerOrderId, orderId, errorMessage, order } = orderResponse;
                    if (!successful) {
                        addOrderAlertMessage({
                            ...request.order[i],
                            status: OrderStatus.FAILED,
                            message: errorMessage || 'Unable to place order'
                        })
                    } else {
                        successCount++;
                        if (order) {
                            updates.push(order)
                            addOrderAlertMessage({
                                ...order,
                                message: 'Order placed successfully'
                            })
                        }
                    }
                })
                updates.length && dispatch(updateOrderHistory({ updates }))
                setRequestInProgress(false);
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
                setRequestInProgress(false)
            }
        }
    }, [dispatch, primaryTradingAccount, tradingAccounts])

    const closeOrReversePosition = useCallback((security: Security, transactionType: TransactionType, quantity: number, reverse = false, product: Product, isBasket: boolean = false) => {
        const batchId = uuid()
        const clientOrderId = uuid()
        setEditOrderData({
            orders: [{
                security,
                transactionType,
                orderType: OrderType.NORMAL,
                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                quantity: (quantity) * (reverse ? 2 : 1),
                isBasket: false,
                batchId,
                orderDate: Date.now(),
                product: product ?? Product.CNC,
                price: 0,
                triggerPrice: 0,
                brokerVariety: Broker_Variety.REGULAR,
                validity: Validity.DAY,
                clientOrderId,
                immediateExecutePaperOrder: false
            }],
            type: reverse ? 'reverse' : 'close'
        })
    }, [])

    const onBasketPositionClose = useCallback(
            async (position: Position) => {
                if (!position) return
                const basketPositions = positions.filter(p => p.basketId === position.basketId && p.closePosition !== 0)

                // // Find and cancel existing SL/Target orders for this basket
                // const basketSlOrder = ordersHistory?.find(o =>
                //     o.orderType === OrderType.STOP_LOSS &&
                //     o.basketId === position.basketId &&
                //     [OrderStatus.OPEN, OrderStatus.TRIGGER_PENDING].includes(o.status)
                // )
                // const basketTargetOrder = ordersHistory?.find(o =>
                //     o.orderType === OrderType.TARGET &&
                //     o.basketId === position.basketId &&
                //     [OrderStatus.OPEN, OrderStatus.TRIGGER_PENDING].includes(o.status)
                // )

                // const ordersToCancel: Order[] = []
                // if (basketSlOrder) ordersToCancel.push(basketSlOrder)
                // if (basketTargetOrder) ordersToCancel.push(basketTargetOrder)

                // // Cancel SL/Target orders if they exist
                // if (ordersToCancel.length > 0) {
                //     await handleEditOrder({
                //         orders: ordersToCancel,
                //         type: 'cancel'
                //     })
                // }

                const orders: Order[] = []
                if(basketPositions.length > 0){
                    const batchId = uuid()
                    const basketId = basketPositions[0].basketId
                    basketPositions.map((pos)=>{
                        const clientOrderId = uuid()
                        const quantity = Math.abs(pos.closePosition)
                        const transactionType: TransactionType = pos.closePosition > 0 ? TransactionType.SELL : TransactionType.BUY
                        orders.push({
                            security: pos.security,
                            transactionType,
                            orderType: OrderType.NORMAL,
                            brokerOrderType: Broker_OrderType.MARKET_ORDER,
                            quantity,
                            isBasket: true,
                            batchId,
                            orderDate: Date.now(),
                            product: pos.product ?? Product.CNC,
                            price: 0,
                            triggerPrice: 0,
                            brokerVariety: Broker_Variety.REGULAR,
                            validity: Validity.DAY,
                            clientOrderId,
                            immediateExecutePaperOrder: false,
                            basketId,
                            strategyGroup: pos.strategyGroup
                        })
                    })
                }
                setEditOrderData({
                    orders,
                    type: 'close'
                })
            },
            [positions, security, handleEditOrder]
        )

    const onChartOrderActionClicked = useCallback((event: CustomEvent) => {
        if (event.type === ChartEvents.CHART_ORDER_ACTION_CLICKED) {
            if (event.detail.actionType === PositionComponentTypes.CONFIRM_BTN) {
                const orders: Order[] = []
                if (event.detail?.slOrder) orders.push(event.detail?.slOrder)
                if (event.detail?.tOrder) orders.push(event.detail?.tOrder)
                if (event.detail?.order) orders.push(event.detail?.order)
                if (orders.length) {
                    handleEditOrder({
                        orders,
                        type: 'place'
                    })
                }
            } else if (event.detail.actionType === PositionComponentTypes.QUANTITY_BTN && event.detail.key.includes('open-order')) {
                console.log(event.detail, 'Add Qunatity Change popup')
            } else if (event.detail.actionType === PositionComponentTypes.CANCEL_BTN || event.detail.actionType === PositionComponentTypes.REVERSE_BTN) {
                const position = event.detail.position as Position
                const isBasket = event.detail.isBasket
                if (position) {
                    const transactionType: TransactionType = position.closePosition < 0 ? TransactionType.SELL : position.closePosition > 0 ? TransactionType.BUY : undefined
                    if(isEmbedded) {
                        if(event.detail.actionType === PositionComponentTypes.REVERSE_BTN) {
                            callNative('onReversePositionDialogClicked', JSON.stringify({
                                tradingAccountId: primaryTradingAccount?.accountId ?? '',
                                order: [{
                                    security: position.security,
                                    transactionType: transactionType,
                                    orderType: OrderType.NORMAL,
                                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                                    quantity: Math.abs(position.closePosition) * 2,
                                    isBasket: false,
                                    batchId: uuid(),
                                    orderDate: Date.now(),
                                    product: position.product ?? Product.CNC,
                                    price: 0,
                                    triggerPrice: 0,
                                    brokerVariety: Broker_Variety.REGULAR,
                                    validity: Validity.DAY,
                                    clientOrderId: uuid()
                                }]
                            }))
                        } else if(event.detail.actionType === PositionComponentTypes.CANCEL_BTN) {
                            callNative('onClosePositionDialogClicked', JSON.stringify({
                                tradingAccountId: primaryTradingAccount?.accountId ?? '',
                                order: [{
                                    security: position.security,
                                    transactionType: transactionType,
                                    orderType: OrderType.NORMAL,
                                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                                    quantity: Math.abs(position.closePosition),
                                    isBasket: false,
                                    batchId: uuid(),
                                    orderDate: Date.now(),
                                    product: position.product ?? Product.CNC,
                                    price: 0,
                                    triggerPrice: 0,
                                    brokerVariety: Broker_Variety.REGULAR,
                                    validity: Validity.DAY,
                                    clientOrderId: uuid()
                                }]
                            }))
                        }
                    } else {
                        if(isBasket){
                            onBasketPositionClose(position)
                            return
                        }
                        closeOrReversePosition(
                            position.security,
                            transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY,
                            Math.abs(position.closePosition),
                            event.detail.actionType === PositionComponentTypes.REVERSE_BTN,
                            position.product,
                            isBasket
                        )
                    }
                } else {
                    const order = event.detail.order as Order
                    if(isEmbedded && order?.orderId) {
                        callNative('onCancelOrdersDialogClicked', JSON.stringify({
                            tradingAccountId: primaryTradingAccount?.accountId ?? '',
                            orderId: [order?.orderId??'']
                        }))
                    } else if (!isEmbedded) {
                        setEditOrderData({
                            orders: [order],
                            type: 'cancel'
                        })
                    }
                }
            }
        }
    }, [closeOrReversePosition, handleEditOrder, isEmbedded, primaryTradingAccount])

    const onChartOrderMarkerClicked = useCallback((event: CustomEvent) => {
        if (isEmbedded) {
            callNative('onChartOrderMarkerClicked', JSON.stringify({
                totalQuantity: event.detail.totalQuantity,
                averagePrice: event.detail.averagePrice,
                type: event.detail.type,
                orders: event.detail.orders.map((order: Order) => ({
                    quantity: order.quantity,
                    price: order.averageTradedPrice,
                    time: order.exchangeTimestamp,
                }))
            }))
        } else {
            setOrdersMarkerData(event.detail)
        }
    }, [isEmbedded])

    useEffect(() => {
        if (chart) {
            chart.subscribeEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, onChartOrderActionClicked)
            chart.subscribeEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, onChartOrderMarkerClicked)
        }

        return () => {
            if (chart) {
                chart.unsubscribeEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, onChartOrderActionClicked)
                chart.unsubscribeEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, onChartOrderMarkerClicked)
            }
        }
    }, [chart, onChartOrderActionClicked, onChartOrderMarkerClicked])

    return (
        <div style={{ position: 'absolute', top: 0, left: 0 }}>
            <ReusableDialog
                title={
                    editOrderData?.type === 'cancel' ?
                        "Cancel Order" :
                        editOrderData?.type === 'close' ?
                            "Close Position" :
                            editOrderData?.type === 'reverse' ?
                                "Reverse Position" : ""
                }
                open={Boolean(editOrderData)}
                onClose={() => setEditOrderData(undefined)}
                cancelText="No"
                submitText={'Yes'}
                onSubmit={() => handleEditOrder(editOrderData)}
                content={
                    editOrderData?.type === 'cancel' ?
                        "Are you sure you want to cancel the order?" :
                        editOrderData?.type === 'close' ?
                            "Are you sure you want to close position?" :
                            editOrderData?.type === 'reverse' ?
                                "Are you sure you want to reverse position?" : ""
                }
            />
            {
                ordersMarkerData ? (
                    <ExtraWideTooltip open={true} placement="top-start" title={(
                        <Card className="font-feature-num" sx={{ borderLeft: theme => `4px solid ${theme.palette?.[ordersMarkerData.type]?.main}` }}>
                            <CardHeader
                                sx={{
                                    '& .MuiCardHeader-avatar': {
                                        mr: '10px'
                                    }
                                }}
                                avatar={
                                    <Avatar aria-label="contract-switch" sx={{
                                        backgroundColor: theme => theme.palette?.[ordersMarkerData.type]?.main,
                                        width: 24,
                                        height: 24,
                                    }}>
                                        <Typography variant="h6" fontSize={'14px'} sx={{ color: 'white' }}>{ordersMarkerData.totalQuantity}</Typography>
                                    </Avatar>
                                }
                                title={<Typography variant="h6" fontSize={'18px'}>{ordersMarkerData.type === 'buy' ? 'Buy' : 'Sell'}</Typography>} />
                            <CardContent sx={{ minWidth: 350, display: 'flex', flexDirection: 'column', gap: '4px', pt: 0 }}>
                                <Box sx={{ display: 'flex', width: '100%', pb: ordersMarkerData.orders.length > 1 ? '10px' : 0 }}>
                                    <Typography color={'textSecondary'} fontSize={'14px'}>{ordersMarkerData.totalQuantity} @ {(ordersMarkerData.averagePrice / (security?.priceMultiplier ?? 100)).toFixed(2)} avg price</Typography>
                                </Box>
                                {
                                    ordersMarkerData.orders.length > 1 ? (
                                        <>
                                            <Box sx={{ display: 'flex', width: '100%' }}>
                                                <Typography color={'textSecondary'} fontSize={'14px'} fontWeight={'bold'}>TRADES</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                                                {
                                                    ordersMarkerData.orders.map((order) => (
                                                        <Typography color={'textSecondary'} fontSize={'14px'}>{`${order.quantity} @ ${(order.averageTradedPrice / (security?.priceMultiplier ?? 100))?.toFixed(2)} ${getDisplayDateTime(order.exchangeTimestamp)}`}</Typography>
                                                    ))
                                                }
                                            </Box>
                                        </>
                                    ) : null
                                }
                            </CardContent>
                        </Card>
                    )} sx={{ margin: '0px !important', padding: '0px !important' }}>
                        <Box sx={{ position: 'absolute', left: ordersMarkerData?.x, top: ordersMarkerData?.y }} />
                    </ExtraWideTooltip>
                ) : null
            }
        </div>
    )
}

export default OrderActionsOnChart